import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { PackageCheck, CheckCircle2, Settings as SettingsIcon } from "lucide-react";

const _getUser = () => {
  try { return JSON.parse(localStorage.getItem("user") || "{}"); }
  catch { return {}; }
};

// Iter 259 — Pack Team / Bulk Labels step pings.
// Polls every 60s; `due` IDs become sticky 60-second toasts. Server
// enforces the 10-minute re-pop cadence via `shown_at`.
const POLL_MS = 60_000;

const STEP_META = {
  pack_team: {
    label: "Pack Team",
    tint: "bg-amber-100 text-amber-800 border-amber-300",
    toastClass: "border-l-4 border-amber-500 bg-amber-50",
  },
  bulk_labels: {
    label: "Bulk Labels",
    tint: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300",
    toastClass: "border-l-4 border-fuchsia-500 bg-fuchsia-50",
  },
};

const InvoiceStepNotifier = () => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [picked, setPicked] = useState({ pack_team: [], bulk_labels: [] });
  const [savingSettings, setSavingSettings] = useState(false);
  const shownNowRef = useRef(new Set()); // avoid duplicate toasts per tick

  const isAdmin = useMemo(
    () => (_getUser().role || "").toLowerCase() === "admin", []);

  const ack = useCallback(async (id) => {
    try {
      await axios.post(`${API}/invoice-notifications/${id}/ack`,
        {}, { headers: getAuthHeader() });
    } catch { /* ignore */ }
    setItems(prev => prev.filter(n => n.id !== id));
    toast.dismiss(`invnotif-${id}`);
  }, []);

  const markShown = useCallback(async (id) => {
    try {
      await axios.post(`${API}/invoice-notifications/${id}/shown`,
        {}, { headers: getAuthHeader() });
    } catch { /* ignore */ }
  }, []);

  const showToast = useCallback((n) => {
    const meta = STEP_META[n.step] || STEP_META.pack_team;
    toast(n.message, {
      id: `invnotif-${n.id}`,
      duration: 60_000,       // sticky for 1 minute (user's SLA)
      className: meta.toastClass,
      icon: <PackageCheck className="w-5 h-5" />,
      description: `${meta.label} · Invoice ${n.invoice_no}`,
      action: {
        label: "Mark Done",
        onClick: () => ack(n.id),
      },
    });
    markShown(n.id);
  }, [ack, markShown]);

  const poll = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/invoice-notifications/pending`,
        { headers: getAuthHeader() });
      const pending = r.data?.pending || [];
      const due = new Set(r.data?.due || []);
      setItems(pending);
      // Iter 148 — cap sticky popups: a busy day can have 20+ pending pings,
      // which flooded the screen (26 stacked toasts) and churned the console.
      // Show at most 3 individual toasts + ONE summary for the rest; every
      // ping still stays listed in the bell dropdown.
      const MAX_STICKY = 3;
      const pop = (n) => {
        shownNowRef.current.add(n.id);
        // allow re-popping after the server re-marks it due (>10 min)
        setTimeout(() => shownNowRef.current.delete(n.id), 60 * 1000);
      };
      const dueNow = pending.filter(n => due.has(n.id) && !shownNowRef.current.has(n.id));
      dueNow.slice(0, MAX_STICKY).forEach(n => { pop(n); showToast(n); });
      if (dueNow.length > MAX_STICKY) {
        dueNow.slice(MAX_STICKY).forEach(n => { pop(n); markShown(n.id); });
        toast(`${dueNow.length - MAX_STICKY} more invoice ping(s) pending`, {
          id: "invnotif-summary",
          duration: 60_000,
          icon: <PackageCheck className="w-5 h-5" />,
          description: "Open the bell in the header to review them all",
        });
      }
    } catch { /* silent — auth may be missing */ }
  }, [showToast]);

  useEffect(() => {
    poll();
    const t = setInterval(poll, POLL_MS);
    return () => clearInterval(t);
  }, [poll]);

  // Admin-only: load user list + current assignees when the dialog opens.
  const openSettings = useCallback(async () => {
    setOpen(false);
    try {
      const [u, s] = await Promise.all([
        axios.get(`${API}/admin/all-users`, { headers: getAuthHeader() }),
        axios.get(`${API}/invoice-notifications/settings`,
          { headers: getAuthHeader() }),
      ]);
      const list = (Array.isArray(u.data) ? u.data : (u.data?.users || []))
        .filter(x => x.approved !== false);
      setUsers(list);
      setPicked({
        pack_team: s.data?.pack_team || [],
        bulk_labels: s.data?.bulk_labels || [],
      });
      setSettingsOpen(true);
    } catch (e) {
      toast.error("Could not load settings");
    }
  }, []);

  const togglePick = (step, uid) => {
    setPicked(prev => {
      const cur = new Set(prev[step] || []);
      if (cur.has(uid)) cur.delete(uid); else cur.add(uid);
      return { ...prev, [step]: Array.from(cur) };
    });
  };

  const saveSettings = async () => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/invoice-notifications/settings`,
        picked, { headers: getAuthHeader() });
      toast.success("Assignees updated");
      setSettingsOpen(false);
    } catch {
      toast.error("Save failed");
    } finally {
      setSavingSettings(false);
    }
  };

  const count = items.length;

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="relative"
                data-testid="invoice-notif-bell">
          <PackageCheck className="w-4 h-4" />
          {count > 0 && (
            <Badge className="absolute -top-1 -right-1 h-4 min-w-[16px] px-1 bg-amber-600 hover:bg-amber-600"
                   data-testid="invoice-notif-count">
              {count}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-96 max-h-[70vh] overflow-y-auto"
                           data-testid="invoice-notif-dropdown">
        <div className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600 border-b flex items-center justify-between">
          <span>Invoice Step Pings</span>
          {isAdmin && (
            <Button size="sm" variant="ghost" className="h-6 px-1.5 text-slate-500"
                    onClick={openSettings}
                    data-testid="invoice-notif-settings-btn"
                    title="Admin — assign Pack Team / Bulk Labels users">
              <SettingsIcon className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
        {count === 0 ? (
          <div className="px-3 py-6 text-center text-sm text-slate-500">
            Nothing pending for you right now.
          </div>
        ) : items.map(n => {
          const meta = STEP_META[n.step] || STEP_META.pack_team;
          return (
            <div key={n.id}
                 className="px-3 py-2 border-b last:border-b-0 flex items-start gap-2"
                 data-testid={`invoice-notif-row-${n.invoice_no}-${n.step}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded border ${meta.tint}`}>
                    {meta.label}
                  </span>
                  <span className="text-xs font-mono text-slate-700">
                    {n.invoice_no}
                  </span>
                </div>
                <div className="text-sm text-slate-800 mt-0.5 leading-snug">
                  {n.message}
                </div>
              </div>
              <Button size="sm" variant="ghost"
                      className="text-emerald-700 hover:bg-emerald-50 shrink-0 h-7"
                      onClick={() => ack(n.id)}
                      data-testid={`invoice-notif-ack-${n.id}`}
                      title="Mark done">
                <CheckCircle2 className="w-4 h-4" />
              </Button>
            </div>
          );
        })}
      </DropdownMenuContent>
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-2xl" data-testid="invoice-notif-settings-dialog">
          <DialogHeader>
            <DialogTitle>Invoice-step notification assignees</DialogTitle>
            <DialogDescription>
              Pick who gets a ping. Everyone selected sees a sticky toast
              + bell entry; the alert re-pops every 10 minutes until all
              assignees click <strong>Mark Done</strong> or the next
              step auto-closes it.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            {["pack_team", "bulk_labels"].map(step => (
              <div key={step} className="border rounded p-2"
                   data-testid={`invoice-notif-settings-${step}`}>
                <div className="flex items-center gap-1.5 mb-2">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded border ${STEP_META[step].tint}`}>
                    {STEP_META[step].label}
                  </span>
                  <span className="text-xs text-slate-500">
                    {(picked[step] || []).length} selected
                  </span>
                </div>
                <div className="max-h-72 overflow-y-auto space-y-1">
                  {users.map(u => {
                    const checked = (picked[step] || []).includes(u.id);
                    return (
                      <label key={u.id}
                             className="flex items-center gap-2 text-sm py-1 px-1 rounded hover:bg-slate-50 cursor-pointer"
                             data-testid={`invoice-notif-settings-${step}-user-${u.id}`}>
                        <input type="checkbox" checked={checked}
                               onChange={() => togglePick(step, u.id)} />
                        <span className="truncate">{u.name}</span>
                        <span className="text-[10px] text-slate-400 truncate">
                          {u.email}
                        </span>
                      </label>
                    );
                  })}
                  {users.length === 0 && (
                    <div className="text-xs text-slate-400 py-6 text-center">
                      No users loaded.
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSettingsOpen(false)}
                    data-testid="invoice-notif-settings-cancel">Cancel</Button>
            <Button onClick={saveSettings} disabled={savingSettings}
                    data-testid="invoice-notif-settings-save">
              {savingSettings ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DropdownMenu>
  );
};

export default InvoiceStepNotifier;
